import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    TextField,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Button,
    Box,
    FormHelperText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Autocomplete,
    Alert,
    AlertTitle
} from '@mui/material';
import { getUserProfile } from '../../../utils/auth';
import { createSRTicket } from '../../../utils/api';
import { useCredentials } from '../../../contexts/CredentialsContext';
import configData from '../../../tds.config.json';

const REDIRECTED_URL = 'https://forms.office.com/pages/responsepage.aspx?id=dokEvxBxh06W88Z0SQi4vke8ZGYXkLhHmxUMf9L6HSpUMjRDQzNLRUpFVVhKMUVNUjdXTE5JR0Y5WS4u&origin=lprLink&route=shorturl'
const QUESTION = 'Got a minute? Help us to improve TDS chatbot by filling in the feedback form!'
const FEEDBACK_MESSAGE = 'Looks like something didn’t work out the way it should. Want to tell us what happened? We value your feedback.'

const getHodMapping = () => {
    const env = process.env.REACT_APP_MODE === 'prod' ? 'prod' : 'dev';
    return configData[env].hodEmailMapping;
};

const getRequesterDefaults = () => {
    const env = process.env.REACT_APP_MODE === 'prod' ? 'prod' : 'dev';
    return {
        requesterName: configData[env].requesterName,
        requesterEmail: configData[env].requesterEmail,
        requesterContact: configData[env].requesterContactNumber
    };
};

const CHECKBOX_OPTIONS = {
    postpaid: {
        testbed: [
            { name: "vobbTest", label: "VOBB Test" },
            { name: "msisdnTest", label: "MSISDN Test" },
            { name: "esimTest", label: "ESIM Test" },
            { name: "blanksimTest", label: "Blanksim Test" }
        ],
        production: [
            { name: "blanksim", label: "Blanksim" },
            { name: "msisdn", label: "MSISDN" },
            { name: "vobb", label: "VOBB" }
        ]
    },
    prepaid: {
        testbed: [
            { name: "msisdnTest", label: "MSISDN Test" },
            { name: "prepaidstarterpackTest", label: "Prepaid Starterpack Test" },
            { name: "softpinTest", label: "Softpin Test" },
            { name: "blanksimTest", label: "Blanksim Test" }
        ],
        production: [
            { name: "blanksim", label: "Blanksim" },
            { name: "softpin", label: "Softpin" },
            { name: "prepaidstarterpack", label: "Prepaid Starterpack" },
            { name: "reloadchargecard", label: "Reload Charge Card" }
        ]
    }
};

// Email validation helper function
const isValidEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const allowedDomains = ['celcomdigi.com', 'digi.com.my', 'corp.digi.com.my'];

    if (!emailPattern.test(email)) return false;

    const domain = email.split('@')[1].toLowerCase();
    return allowedDomains.includes(domain);
};

/**
 * A common requester form component that works for both Postpaid and Prepaid forms
 * 
 * @param {Object} props - Component props
 * @param {string} props.formType - Either "postpaid" or "prepaid" to determine form behavior
 * @param {Function} props.onSubmit - Callback function when form is submitted
 * @param {Object} props.initialData - Initial data from test data form
 * @param {Object} props.requesterData - Initial requester data
 */
const CommonRequesterForm = ({ formType, onSubmit, initialData, requesterData, ws }) => {
    console.log('[CommonRequesterForm] Props received:', {
        formType,
        initialData,
        requesterData,
        initialData_requesterName: initialData?.requesterName,
        requesterData_requesterName: requesterData?.requesterName
    });

    const credentials = useCredentials();

    // Get checkbox options from static object instead of useMemo
    const checkboxOptions = CHECKBOX_OPTIONS[formType];

    // Get default requester info from config
    const requesterDefaults = getRequesterDefaults();

    // Debug: Check if departments are loaded
    console.log('[CommonRequesterForm] Departments from config:', configData.departments?.length, 'departments');

    const initialFormState = {
        requesterName: requesterData?.requesterName || requesterDefaults.requesterName,
        requesterEmail: requesterData?.requesterEmail || requesterDefaults.requesterEmail,
        requesterContact: requesterData?.requesterContact || requesterDefaults.requesterContact,
        lineOwnerName: requesterData?.lineOwnerName || '',
        lineOwnerEmail: requesterData?.lineOwnerEmail || '',
        division: requesterData?.division || '',
        environment: initialData?.environment || '',
        department: requesterData?.department || '',
        hodName: requesterData?.hodName || '',
        hodEmail: requesterData?.hodEmail || (requesterData?.hodName ? getHodMapping()[requesterData?.hodName] : ''),
        projectName: requesterData?.projectName || '',
        purpose: requesterData?.purpose || '',
        location: requesterData?.location || '',
        testingDate: requesterData?.testingDate || new Date().toISOString().split('T')[0],
    };

    [...checkboxOptions.testbed, ...checkboxOptions.production].forEach(option => {
        initialFormState[option.name] = initialData?.[option.name] ?? false;
    });

    const [formData, setFormData] = useState(initialFormState);

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
    const [successDialog, setSuccessDialog] = useState({ open: false, message: '', ticketNo: '' });

    useEffect(() => {
        const fetchUserProfile = async () => {
            const response = await getUserProfile();

            // Use department and userId from user profile
            // userId is used for both line owner name and email
            const { department, userId } = response;
            setFormData(prevData => ({
                ...prevData,
                department,
                lineOwnerName: userId || prevData.lineOwnerName,
                lineOwnerEmail: userId || prevData.lineOwnerEmail
            }));
        };

        fetchUserProfile();
    }, []);

    // WebSocket listener only for bedrockSRTicket (form data population)
    useEffect(() => {
        const mapRequesterFormData = (data) => {
            // Common fields
            const mappedData = {
                requesterName: data.requesterName || '',
                requesterEmail: data.requesterEmail || '',
                requesterContact: data.requesterContact || '',
                lineOwnerName: data.lineOwnerName || '',
                lineOwnerEmail: data.lineOwnerEmail || '',
                division: data.division || '',
                environment: data.environment || '',
                department: data.department || '',
                hodName: data.hodName || '',
                hodEmail: data.hodEmail || (data.hodName ? getHodMapping()[data.hodName] : ''),
                projectName: data.projectName || data.cr_project_name || '',
                purpose: data.purpose || '',
                location: data.location || '',
                testingDate: data.testingDate || new Date().toISOString().split('T')[0],
            };

            [...checkboxOptions.testbed, ...checkboxOptions.production].forEach(option => {
                mappedData[option.name] = data[option.name] ?? false;
            });

            return mappedData;
        };

        const handleWebSocketMessage = (event) => {
            const response = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

            // Only handle bedrockSRTicket for form data population
            if (response.type === 'bedrockSRTicket' && response.requesterFormData) {
                const mapped = mapRequesterFormData(response.requesterFormData);
                setFormData(prevData => ({
                    ...prevData,
                    ...mapped
                }));
            }
        };

        if (ws) {
            // Remove any existing listeners before adding new one
            ws.removeEventListener('message', handleWebSocketMessage);
            ws.addEventListener('message', handleWebSocketMessage);
        }

        return () => {
            if (ws) {
                ws.removeEventListener('message', handleWebSocketMessage);
            }
        };
    }, [ws, formType]);


    // Update formData with requesterData and initialData
    useEffect(() => {
        const updates = {};

        // Handle requesterData updates
        if (requesterData) {
            Object.assign(updates, {
                requesterName: requesterData?.requesterName || '',
                requesterEmail: requesterData?.requesterEmail || '',
                requesterContact: requesterData?.requesterContact || '',
                lineOwnerName: requesterData?.lineOwnerName || '',
                lineOwnerEmail: requesterData?.lineOwnerEmail || '',
                division: requesterData?.division || '',
                department: requesterData?.department || '',
                hodName: requesterData?.hodName || '',
                hodEmail: requesterData?.hodEmail || (requesterData?.hodName ? getHodMapping()[requesterData?.hodName] : ''),
                purpose: requesterData?.purpose || '',
                location: requesterData?.location || '',
                testingDate: requesterData?.testingDate || formData.testingDate || new Date().toISOString().split('T')[0]
            });

            // Only update environment if not already set by initialData
            if (requesterData.environment && !initialData?.environment) {
                updates.environment = requesterData.environment;
            }
        }

        // Handle initialData updates (testbed/production options and environment)
        if (initialData) {
            console.log('[CommonRequesterForm] Received initialData:', initialData);

            // Get the environment to determine which checkboxes to set
            const environment = initialData.environment?.toLowerCase() || '';
            const isTestbed = environment === 'testbed';
            console.log('[CommonRequesterForm] Environment:', environment, 'isTestbed:', isTestbed);

            // Create checkbox state based on environment
            const checkboxState = {};

            // Only set appropriate checkboxes based on the environment
            if (isTestbed) {
                // Set testbed options
                checkboxOptions.testbed.forEach(option => {
                    checkboxState[option.name] = initialData?.[option.name] ?? false;
                });
                // Ensure production options are off in testbed
                checkboxOptions.production.forEach(option => {
                    checkboxState[option.name] = false;
                });
            } else {
                console.log('[CommonRequesterForm] Setting PRODUCTION checkboxes from initialData');
                // Set production options
                checkboxOptions.production.forEach(option => {
                    checkboxState[option.name] = initialData?.[option.name] ?? false;
                    console.log(`[CommonRequesterForm] Checkbox ${option.name} = ${checkboxState[option.name]} (from initialData: ${initialData?.[option.name]})`);
                });
                // Ensure testbed options are off in production
                checkboxOptions.testbed.forEach(option => {
                    checkboxState[option.name] = false;
                });
            }

            // Add initialData updates - include ALL fields that bedrock might send
            Object.assign(updates, {
                requesterName: initialData?.requesterName || updates.requesterName,
                requesterEmail: initialData?.requesterEmail || updates.requesterEmail,
                requesterContact: initialData?.requesterContact || updates.requesterContact,
                lineOwnerName: initialData?.lineOwnerName || updates.lineOwnerName,
                lineOwnerEmail: initialData?.lineOwnerEmail || updates.lineOwnerEmail,
                division: initialData?.division || updates.division,
                department: initialData?.department || updates.department,
                hodName: initialData?.hodName || updates.hodName,
                hodEmail: initialData?.hodEmail || (initialData?.hodName ? getHodMapping()[initialData.hodName] : updates.hodEmail),
                projectName: initialData?.projectName || '',
                purpose: initialData?.purpose || updates.purpose,
                location: initialData?.location || updates.location,
                testingDate: initialData?.testingDate || updates.testingDate || formData.testingDate || new Date().toISOString().split('T')[0],
                environment: initialData?.environment || '',
                ...checkboxState
            });

            console.log('[CommonRequesterForm] Updates to apply:', updates);
        }

        // Apply all updates at once if there are any
        if (Object.keys(updates).length > 0) {
            setFormData(prev => ({
                ...prev,
                ...updates
            }));
        }
    }, [requesterData, initialData, formType]);

    const validateField = (name, value) => {
        // Required field validation
        if (!value || (typeof value === 'string' && !value.trim())) {
            if (['requesterName', 'requesterEmail', 'requesterContact',
                'lineOwnerName', 'lineOwnerEmail', 'division', 'environment',
                'department', 'hodName', 'projectName', 'purpose', 'location',
                'testingDate'].includes(name)) {
                return 'This field is required';
            }
        }

        // Email validation
        if (name === 'requesterEmail' || name === 'lineOwnerEmail') {
            if (value && !isValidEmail(value.trim())) {
                return 'Invalid email format must be in the domain of celcomdigi.com, digi.com.my or corp.digi.com.my';
            }
        }

        // Phone number validation
        if (name === 'requesterContact' && value) {
            const cleanedNumber = value.replace(/[- ]/g, '');
            if (!/^\d{10,12}$/.test(cleanedNumber)) {
                return 'Invalid phone number format (10-12 digits)';
            }
        }

        // Purpose validation
        if (name === 'purpose') {
            const length = value.trim().length;
            if (length < 20) {
                return 'Purpose must be at least 20 characters long';
            }
            if (length > 50) {
                return 'Purpose must not be more than 50 characters';
            }
        }

        return '';
    };

    // Helper function to handle checkbox changes
    const handleCheckboxChange = (name, checked) => {
        // Special case for blanksim options
        if (name === 'blanksim' || name === 'blanksimTest') {
            if (checked) {
                // When blanksim is checked, uncheck all others
                const updatedState = {};

                // Uncheck all options first
                [...checkboxOptions.testbed, ...checkboxOptions.production].forEach(option => {
                    updatedState[option.name] = false;
                });

                // Then check just the blanksim option
                updatedState[name] = checked;

                return updatedState;
            }
            // Just uncheck this option
            return { [name]: checked };
        } else if (checked && (formData.blanksim || formData.blanksimTest)) {
            // Can't check other options when blanksim is checked
            return null;
        } else {
            // For other checkboxes, uncheck blanksim options
            return {
                [name]: checked,
                blanksim: false,
                blanksimTest: false
            };
        }
    };

    // Main input change handler
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            // Get the updated checkbox state
            const updatedState = handleCheckboxChange(name, checked);

            // If null is returned, no change should be made
            if (updatedState) {
                setFormData(prev => ({
                    ...prev,
                    ...updatedState
                }));
                validateTestTypeSelection();
            }
        } else {
            // For HOD name selection, also update the HOD email
            if (name === 'hodName') {
                const hodEmail = getHodMapping()[value] || '';
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    hodEmail: hodEmail
                }));
            } else {
                // Handle other text input fields
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }

            // Validate the field immediately
            const errorMessage = validateField(name, value);
            setErrors(prev => ({
                ...prev,
                [name]: errorMessage
            }));
        }
    };

    // Function to validate test type selection
    const validateTestTypeSelection = () => {
        // Clear any previous testType error first
        setErrors(prev => ({ ...prev, testType: '' }));

        setTimeout(() => {
            const isTestbed = formData.environment && formData.environment.toLowerCase() === 'testbed';

            // Check if any checkbox in the correct environment group is selected
            const testbedSelected = checkboxOptions.testbed.some(option => formData[option.name]);
            const productionSelected = checkboxOptions.production.some(option => formData[option.name]);

            if ((isTestbed && !testbedSelected) || (!isTestbed && !productionSelected)) {
                setErrors(prev => ({
                    ...prev,
                    testType: 'At least one request type must be selected'
                }));
            }
        }, 100);  // Small delay to ensure formData is updated
    };

    const scrollToFirstError = () => {
        const errorElements = document.querySelectorAll('[data-error="true"]');
        if (errorElements.length > 0) {
            errorElements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const validateForm = () => {
        console.log('Starting validation for create sr ticket...');
        console.log('Form data at validation for create sr ticket:', formData);

        // Get all required fields
        const requiredFields = [
            'division', 'environment',
            'department', 'hodName', 'projectName', 'purpose', 'location',
            'testingDate'
        ];

        console.log("testingDate value:", formData.testingDate);
        console.log("location value:", formData.location);

        // Initialize new errors object and create a map of field errors
        const newErrors = Object.fromEntries(
            requiredFields.map(field => {
                const errorMessage = validateField(field, formData[field]);
                if (errorMessage) {
                    console.log(`[CommonRequesterForm] Field error - ${field}: ${errorMessage}`);
                }
                return [field, errorMessage];
            }).filter(([_, errorMessage]) => errorMessage !== '')
        );

        // Check test type selections
        const isTestbed = formData.environment && formData.environment.toLowerCase() === 'testbed';
        console.log('[CommonRequesterForm] Environment:', formData.environment, 'isTestbed:', isTestbed);

        // Check if any checkbox in the correct environment group is selected
        const testbedSelected = checkboxOptions.testbed.some(option => formData[option.name]);
        const productionSelected = checkboxOptions.production.some(option => formData[option.name]);

        console.log('[CommonRequesterForm] Testbed checkboxes:', checkboxOptions.testbed.map(opt => ({ name: opt.name, checked: formData[opt.name] })));
        console.log('[CommonRequesterForm] Production checkboxes:', checkboxOptions.production.map(opt => ({ name: opt.name, checked: formData[opt.name] })));
        console.log('[CommonRequesterForm] testbedSelected:', testbedSelected, 'productionSelected:', productionSelected);

        if ((isTestbed && !testbedSelected) || (!isTestbed && !productionSelected)) {
            console.log('[CommonRequesterForm] Checkbox validation FAILED - no checkbox selected');
            newErrors.testType = 'At least one request type must be selected';
        }

        setErrors(newErrors);

        const isValid = Object.keys(newErrors).length === 0;
        console.log('[CommonRequesterForm] Total errors:', Object.keys(newErrors).length, newErrors);

        if (!isValid) {
            setTimeout(() => scrollToFirstError(), 100);
        }

        return isValid;
    };

    const handleCreateSRTicket = async () => {
        console.log('[CommonRequesterForm] Create SR Ticket clicked');
        console.log('[CommonRequesterForm] Current form data:', formData);

        const isValid = validateForm();
        console.log('[CommonRequesterForm] Validation result:', isValid);
        console.log('[CommonRequesterForm] Validation errors:', errors);

        if (!isValid) {
            console.log('[CommonRequesterForm] Validation failed, not creating ticket');
            return false;
        }

        console.log('[CommonRequesterForm] Validation passed, creating ticket...');
        setIsLoading(true);

        const requesterData = {
            requesterName: formData.requesterName,
            requesterEmail: formData.requesterEmail,
            requesterContact: formData.requesterContact,
            lineOwnerName: formData.lineOwnerName,
            lineOwnerEmail: formData.lineOwnerEmail,
            division: formData.division,
            environment: formData.environment,
            department: formData.department,
            hodName: formData.hodName,
            hodEmail: formData.hodEmail,
            projectName: formData.projectName,
            purpose: formData.purpose,
            location: formData.location,
            testingDate: formData.testingDate
        };

        // Add only the relevant test types based on environment
        const isTestbed = formData.environment.toLowerCase() === 'testbed';

        if (isTestbed) {
            checkboxOptions.testbed.forEach(option => {
                requesterData[option.name] = formData[option.name] || false;
            });
        } else {
            checkboxOptions.production.forEach(option => {
                requesterData[option.name] = formData[option.name] || false;
            });
        }

        const form_id = initialData?.formId;

        try {
            // Use HTTP API instead of WebSocket
            const response = await createSRTicket(requesterData, initialData, form_id, formType, credentials);

            setIsLoading(false);

            // Handle success response
            if (response.ticket_number || response.TicketNo) {
                const ticketNumber = response.ticket_number || response.TicketNo;
                const successMessage = response.success_message || response.Message || 'SR Ticket created successfully!';

                // Update success dialog
                setSuccessDialog({
                    open: true,
                    message: successMessage,
                    ticketNo: ticketNumber
                });

                return true;
            } else {
                // Handle API error response
                const errorMessage = response.error || response.err_message || 'Failed to create SR ticket';
                setErrorDialog({
                    open: true,
                    message: errorMessage
                });
                return false;
            }
        } catch (error) {
            console.error('❌ SR Ticket Creation Failed:', error);
            setIsLoading(false);

            setErrorDialog({
                open: true,
                message: `Error creating SR ticket: ${error.message}. Please try again.`
            });

            return false;
        }
    };

    return (
        <Box sx={{ maxWidth: 800, margin: 'auto', mt: 4 }}>
            <TableContainer component={Paper}>
                <Table>
                    <TableBody>
                        <TableRow
                            data-error={!!errors.requesterName}
                            sx={{
                                backgroundColor: errors.requesterName ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                                display: 'none'
                            }}
                        >
                            <TableCell>Requester Name *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name="requesterName"
                                    value={formData.requesterName}
                                    onChange={handleInputChange}
                                    error={!!errors.requesterName}
                                    helperText={errors.requesterName}
                                    placeholder="Enter your full name"
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.requesterEmail}
                            sx={{
                                backgroundColor: errors.requesterEmail ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                                display: 'none'
                            }}
                        >
                            <TableCell>Requester Email *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    type="email"
                                    name="requesterEmail"
                                    value={formData.requesterEmail}
                                    onChange={handleInputChange}
                                    error={!!errors.requesterEmail}
                                    helperText={errors.requesterEmail}
                                    placeholder="example@celcomdigi.com"
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.requesterContact}
                            sx={{
                                backgroundColor: errors.requesterContact ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                                display: 'none'
                            }}
                        >
                            <TableCell>Requester Contact Number *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    type="tel"
                                    name="requesterContact"
                                    value={formData.requesterContact}
                                    onChange={handleInputChange}
                                    error={!!errors.requesterContact}
                                    helperText={errors.requesterContact}
                                    placeholder="10-12 digits (e.g., 0123456789)"
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.lineOwnerName}
                            sx={{
                                backgroundColor: errors.lineOwnerName ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                                display: 'none'
                            }}
                        >
                            <TableCell>Test Line Owner Name *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name="lineOwnerName"
                                    value={formData.lineOwnerName}
                                    onChange={handleInputChange}
                                    error={!!errors.lineOwnerName}
                                    helperText={errors.lineOwnerName}
                                    placeholder="Enter line owner's full name"
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.lineOwnerEmail}
                            sx={{
                                backgroundColor: errors.lineOwnerEmail ? 'rgba(255, 0, 0, 0.05)' : 'inherit',
                                display: 'none'
                            }}
                        >
                            <TableCell>Test Line Owner Email *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    type="email"
                                    name="lineOwnerEmail"
                                    value={formData.lineOwnerEmail}
                                    onChange={handleInputChange}
                                    error={!!errors.lineOwnerEmail}
                                    helperText={errors.lineOwnerEmail}
                                    placeholder="example@celcomdigi.com"
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.division}
                            sx={{
                                backgroundColor: errors.division ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>Division *</TableCell>
                            <TableCell>
                                <Select
                                    fullWidth
                                    name="division"
                                    value={formData.division}
                                    onChange={handleInputChange}
                                    error={!!errors.division}
                                >
                                    {configData.divisions.map((division) => (
                                        <MenuItem key={division} value={division}>
                                            {division}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.division && (
                                    <FormHelperText error>{errors.division}</FormHelperText>
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.environment}
                            sx={{
                                backgroundColor: errors.environment ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>Environment *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name="environment"
                                    value={formData.environment}
                                    disabled
                                    error={!!errors.environment}
                                    helperText={errors.environment}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.department}
                            sx={{
                                backgroundColor: errors.department ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>Department *</TableCell>
                            <TableCell>
                                <Autocomplete
                                    fullWidth
                                    options={Array.isArray(configData.departments) ? configData.departments : []}
                                    value={formData.department && formData.department !== '' ? formData.department : null}
                                    onChange={(event, newValue) => {
                                        handleInputChange({
                                            target: {
                                                name: 'department',
                                                value: newValue || ''
                                            }
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            error={!!errors.department}
                                            helperText={errors.department}
                                            placeholder="Search or select department"
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    getOptionLabel={(option) => option ? String(option) : ''}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.hodName}
                            sx={{
                                backgroundColor: errors.hodName ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>HOD Name *</TableCell>
                            <TableCell>
                                <Select
                                    fullWidth
                                    name="hodName"
                                    value={formData.hodName}
                                    onChange={handleInputChange}
                                    error={!!errors.hodName}
                                >
                                    {Object.keys(getHodMapping()).map((hodName) => (
                                        <MenuItem key={hodName} value={hodName}>
                                            {hodName}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.hodName && (
                                    <FormHelperText error>{errors.hodName}</FormHelperText>
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.hodEmail}
                            sx={{
                                backgroundColor: errors.hodEmail ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>HOD Email (Auto populated)</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    type="email"
                                    name="hodEmail"
                                    value={formData.hodEmail}
                                    slotProps={{
                                        input: {
                                            readOnly: true,
                                        }
                                    }}
                                    disabled
                                    placeholder="example@celcomdigi.com"
                                />
                                <FormHelperText>Note: HOD Email will be auto populated</FormHelperText>
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.projectName}
                            sx={{
                                backgroundColor: errors.projectName ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>Project Name/BAU Process Name *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name="projectName"
                                    value={formData.projectName}
                                    onChange={handleInputChange}
                                    error={!!errors.projectName}
                                    disabled
                                    helperText={errors.projectName}
                                />
                                <FormHelperText>Note: Project Name/BAU Process Name will follow the previous test data form</FormHelperText>
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.purpose}
                            sx={{
                                backgroundColor: errors.purpose ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>Purpose of Line *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    name="purpose"
                                    value={formData.purpose}
                                    onChange={handleInputChange}
                                    error={!!errors.purpose}
                                    helperText={errors.purpose || 'Must be between 20 and 50 characters'}
                                    multiline
                                    rows={3}
                                    placeholder="Describe the purpose in detail (20-50 characters)"
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.location}
                            sx={{
                                backgroundColor: errors.location ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>Location (CD Hub/CD Tower) *</TableCell>
                            <TableCell>
                                <Select
                                    fullWidth
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    error={!!errors.location}
                                >
                                    <MenuItem value="CD Hub">CD Hub</MenuItem>
                                    <MenuItem value="CD Tower">CD Tower</MenuItem>
                                </Select>
                                {errors.location && (
                                    <FormHelperText error>{errors.location}</FormHelperText>
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow
                            data-error={!!errors.testingDate}
                            sx={{
                                backgroundColor: errors.testingDate ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                            }}
                        >
                            <TableCell>Testing Date *</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    type="date"
                                    name="testingDate"
                                    value={formData.testingDate}
                                    onChange={handleInputChange}
                                    error={!!errors.testingDate}
                                    helperText={errors.testingDate}
                                />
                            </TableCell>
                        </TableRow>
                        {formData.environment && (
                            <TableRow
                                data-error={!!errors.testType}
                                sx={{
                                    backgroundColor: errors.testType ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
                                }}
                            >
                                <TableCell>Type of Request ({formData.environment})</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Box>
                                            {formData.environment.toLowerCase() === 'testbed' ? (
                                                <>
                                                    {checkboxOptions.testbed.map((option) => (
                                                        <FormControlLabel
                                                            key={option.name}
                                                            control={<Checkbox checked={formData[option.name]} onChange={handleInputChange} name={option.name} />}
                                                            label={option.label}
                                                            disabled
                                                        />
                                                    ))}
                                                </>
                                            ) : (
                                                <>
                                                    {checkboxOptions.production.map((option) => (
                                                        <FormControlLabel
                                                            key={option.name}
                                                            control={<Checkbox checked={formData[option.name]} onChange={handleInputChange} name={option.name} />}
                                                            label={option.label}
                                                            disabled
                                                        />
                                                    ))}
                                                </>
                                            )}
                                        </Box>
                                        <FormHelperText>Note: Blanksim cannot be selected together with other options</FormHelperText>
                                        {errors.testType && (
                                            <FormHelperText error>{errors.testType}</FormHelperText>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button
                    onClick={handleCreateSRTicket}
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isLoading}
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease'
                    }}
                >
                    {isLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* iPhone-style loading dots */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 1.5
                                }}
                            >
                                {[0, 1, 2].map((dot) => (
                                    <Box
                                        key={dot}
                                        component="span"
                                        sx={{
                                            width: '6px',
                                            height: '6px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            borderRadius: '50%',
                                            margin: '0 2px',
                                            display: 'inline-block',
                                            animation: 'iphoneDotFade 1.4s infinite ease-in-out both',
                                            animationDelay: `${dot * 0.16}s`,
                                            '@keyframes iphoneDotFade': {
                                                '0%, 80%, 100%': {
                                                    transform: 'scale(0.7)',
                                                    opacity: 0.3
                                                },
                                                '40%': {
                                                    transform: 'scale(1.0)',
                                                    opacity: 1
                                                }
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                            Creating SR ticket
                        </Box>
                    ) : 'Create SR ticket'}
                </Button>
            </Box>

            {/* Error Confirmation Dialog */}
            <Dialog
                open={errorDialog.open}
                onClose={() => { }}
                disableEscapeKeyDown
                aria-labelledby="error-dialog-title"
                aria-describedby="error-dialog-description"
                TransitionProps={{
                    timeout: {
                        enter: 400,
                        exit: 300
                    }
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: { xs: '90%', sm: 500 },
                        maxWidth: 600,
                        animation: errorDialog.open ? 'slideInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
                        '@keyframes slideInScale': {
                            '0%': {
                                opacity: 0,
                                transform: 'translateY(-30px) scale(0.9)'
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'translateY(0) scale(1)'
                            }
                        }
                    }
                }}
            >
                <DialogTitle
                    id="error-dialog-title"
                    sx={{
                        pb: 2,
                        pt: 3,
                        px: 3
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor: '#ffebee',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                animation: errorDialog.open ? 'errorShake 0.5s cubic-bezier(.36,.07,.19,.97) 0.2s, errorPulse 2s ease-in-out 0.7s infinite' : 'none',
                                '@keyframes errorShake': {
                                    '0%, 100%': {
                                        transform: 'translateX(0) rotate(0deg)'
                                    },
                                    '10%, 30%, 50%, 70%, 90%': {
                                        transform: 'translateX(-8px) rotate(-5deg)'
                                    },
                                    '20%, 40%, 60%, 80%': {
                                        transform: 'translateX(8px) rotate(5deg)'
                                    }
                                },
                                '@keyframes errorPulse': {
                                    '0%, 100%': {
                                        boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)'
                                    },
                                    '50%': {
                                        boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)'
                                    }
                                }
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    fontSize: '28px',
                                    lineHeight: 1,
                                    animation: errorDialog.open ? 'iconScale 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.2s forwards' : 'none',
                                    '@keyframes iconScale': {
                                        '0%': {
                                            transform: 'scale(0)'
                                        },
                                        '50%': {
                                            transform: 'scale(1.2)'
                                        },
                                        '100%': {
                                            transform: 'scale(1)'
                                        }
                                    }
                                }}
                            >
                                ❌
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Box
                                component="h2"
                                sx={{
                                    m: 0,
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                    color: '#d32f2f',
                                    lineHeight: 1.3
                                }}
                            >
                                Ticket Creation Failed
                            </Box>
                            <Box
                                sx={{
                                    fontSize: '0.875rem',
                                    color: '#666',
                                    mt: 0.5
                                }}
                            >
                                An error occurred while creating your SR ticket
                            </Box>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 2 }}>
                    <Alert
                        severity="error"
                        sx={{
                            mb: 2,
                            animation: errorDialog.open ? 'fadeInUp 0.4s ease-out 0.3s backwards' : 'none',
                            '@keyframes fadeInUp': {
                                '0%': {
                                    opacity: 0,
                                    transform: 'translateY(15px)'
                                },
                                '100%': {
                                    opacity: 1,
                                    transform: 'translateY(0)'
                                }
                            },
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
                            Error Details
                        </AlertTitle>
                        <Box sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '0.875rem',
                            lineHeight: 1.6
                        }}>
                            {errorDialog.message}
                        </Box>
                    </Alert>

                    {errorDialog.message?.includes('not registered') && (
                        <Alert
                            severity="info"
                            sx={{
                                mb: 2,
                                animation: errorDialog.open ? 'fadeInUp 0.4s ease-out 0.5s backwards' : 'none',
                                '@keyframes fadeInUp': {
                                    '0%': {
                                        opacity: 0,
                                        transform: 'translateY(15px)'
                                    },
                                    '100%': {
                                        opacity: 1,
                                        transform: 'translateY(0)'
                                    }
                                }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
                                What should I do?
                            </AlertTitle>
                            Your email needs to be registered in the system before you can create SR tickets.
                            Please contact the administrator to add your email mapping.
                        </Alert>
                    )}

                    <DialogContentText
                        sx={{
                            mt: 2,
                            fontSize: '0.875rem',
                            color: '#666',
                            animation: errorDialog.open ? 'fadeIn 0.4s ease-out 0.6s backwards' : 'none',
                            '@keyframes fadeIn': {
                                '0%': {
                                    opacity: 0
                                },
                                '100%': {
                                    opacity: 1
                                }
                            }
                        }}
                    >
                        💡 Need help? Contact the support team or report this issue using the feedback form.
                    </DialogContentText>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                        pt: 1,
                        gap: 1.5,
                        flexWrap: 'wrap',
                        animation: errorDialog.open ? 'fadeInUp 0.4s ease-out 0.7s backwards' : 'none',
                        '@keyframes fadeInUp': {
                            '0%': {
                                opacity: 0,
                                transform: 'translateY(10px)'
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'translateY(0)'
                            }
                        }
                    }}
                >
                    <Button
                        onClick={() => {
                            window.open(REDIRECTED_URL, '_blank');
                        }}
                        variant="outlined"
                        color="primary"
                        startIcon={
                            <Box component="span" sx={{ fontSize: '18px' }}>
                                📝
                            </Box>
                        }
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            borderWidth: 2,
                            '&:hover': {
                                borderWidth: 2
                            }
                        }}
                    >
                        Report Issue
                    </Button>
                    <Button
                        onClick={() => {
                            setErrorDialog({ ...errorDialog, open: false });
                        }}
                        variant="contained"
                        color="error"
                        autoFocus
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            minWidth: 120
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Confirmation Dialog */}
            <Dialog
                open={successDialog.open}
                onClose={() => { }}
                disableEscapeKeyDown
                aria-labelledby="success-dialog-title"
                aria-describedby="success-dialog-description"
            >
                <DialogTitle id="success-dialog-title" sx={{ color: '#388e3c' }}>
                    {"SR Ticket Created Successfully!"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="success-dialog-description">
                        {successDialog.message}
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 2, color: '#666' }}>
                        Your SR Ticket Number: {successDialog.ticketNo}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            const shouldRedirect = window.confirm(QUESTION);
                            if (shouldRedirect) {
                                window.open(REDIRECTED_URL, '_blank');
                            }
                            // Proceed with original way of clearing form back to chat area
                            setSuccessDialog({ ...successDialog, open: false });
                            if (typeof window.enableFormButtons === 'function') {
                                window.enableFormButtons();
                            }
                            if (onSubmit) {
                                onSubmit(formData);
                            }
                        }}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CommonRequesterForm;